<template>
  <div class="profile-page">
    <div class="profile-bg"></div>

    <header class="profile-header">
      <div>
        <p class="eyebrow">ROBOCOIN 账户中心</p>
        <h1>个人中心</h1>
        <p>查看你的全部订单、已完成服务、机器人钱包和账号资料。进行中的任务只保留实时处理，历史订单统一沉淀到这里。</p>
      </div>
      <div class="header-actions">
        <button class="ghost-btn" @click="$router.push({ name: 'home' })">返回首页</button>
        <button class="primary-btn" @click="refreshAll">刷新数据</button>
      </div>
    </header>

    <main class="profile-layout">
      <aside class="profile-card">
        <div class="avatar-wrap">
          <img v-if="avatarUrl" :src="avatarUrl" alt="avatar" />
          <span v-else>{{ avatarLetter }}</span>
          <label>
            更换头像
            <input type="file" accept="image/*" @change="handleAvatarChange" />
          </label>
        </div>
        <h2>{{ profileForm.username || currentUser.username || 'RoboCoin 用户' }}</h2>
        <p>{{ profileForm.email || currentUser.email || '未绑定邮箱' }}</p>
        <div class="profile-stats">
          <div>
            <span>信誉分</span>
            <strong>{{ profileForm.reputationScore || currentUser.reputationScore || 80 }}</strong>
          </div>
          <div>
            <span>机器人</span>
            <strong>{{ robots.length }}</strong>
          </div>
          <div>
            <span>钱包总额</span>
            <strong>{{ formatCoin(totalRobotCoin) }}</strong>
          </div>
        </div>
        <nav class="profile-menu">
          <button
            v-for="item in tabs"
            :key="item.key"
            :class="{ active: activeTab === item.key }"
            @click="activeTab = item.key"
          >
            <span>{{ item.code }}</span>
            <div>
              <strong>{{ item.label }}</strong>
              <small>{{ item.desc }}</small>
            </div>
          </button>
        </nav>
      </aside>

      <section class="content-panel">
        <div v-if="activeTab === 'orders'" class="panel-block">
          <div class="panel-head">
            <div>
              <p class="eyebrow">订单记录</p>
              <h2>全部订单</h2>
            </div>
            <div class="filter-row">
            <el-select v-model="orderFilters.status" clearable placeholder="订单状态" @change="fetchOrders">
              <el-option label="已接单" value="ACCEPTED"></el-option>
              <el-option label="进行中" value="RUNNING"></el-option>
              <el-option label="已完成" value="FINISHED"></el-option>
              <el-option label="已取消" value="CANCELLED"></el-option>
              <el-option label="仲裁中" value="ARBITRATION"></el-option>
            </el-select>
              <el-select v-model="orderFilters.role" clearable placeholder="我的身份" @change="fetchOrders">
                <el-option label="我是发布者" value="PUBLISHER"></el-option>
                <el-option label="我是服务者" value="TAKER"></el-option>
              </el-select>
            </div>
          </div>

          <div class="metric-grid">
            <article><span>全部订单</span><strong>{{ orderSummary.totalOrders || 0 }}</strong></article>
            <article><span>进行中</span><strong>{{ orderSummary.activeOrders || 0 }}</strong></article>
            <article><span>已完成</span><strong>{{ orderSummary.finishedOrders || 0 }}</strong></article>
            <article><span>仲裁中</span><strong>{{ orderSummary.arbitrationOrders || 0 }}</strong></article>
          </div>

          <el-table v-loading="ordersLoading" :data="orders" class="profile-table">
            <el-table-column prop="id" label="订单ID" width="90"></el-table-column>
            <el-table-column label="任务" min-width="210">
              <template #default="{ row }">
                <strong>{{ row.taskTitle || '未命名任务' }}</strong>
                <small>{{ serviceTypeLabel(row.serviceType) }} · {{ roleLabel(row) }}</small>
              </template>
            </el-table-column>
            <el-table-column label="机器人" min-width="170">
              <template #default="{ row }">
                <span>{{ row.robotName || '-' }}</span>
                <small>{{ robotTypeLabel(row.robotCategory) }}</small>
                <template v-if="row.executionMode === 'SUBCONTRACT'">
                  <span class="segment-arrow">→ {{ row.secondaryRobotName || `机器人 #${row.secondaryRobotId}` }}</span>
                  <small>交接：{{ row.handoverAddress || '待补充' }} · {{ row.primaryRewardPercent }}% / {{ 100 - row.primaryRewardPercent }}%</small>
                </template>
              </template>
            </el-table-column>
            <el-table-column label="结算" min-width="210">
              <template #default="{ row }">
                <span>预付 {{ formatCoin(row.prepaidRobocoin) }}</span>
                <small>应付 {{ formatCoin(row.finalRobocoin) }} / 退 {{ formatCoin(row.settlementRefundRobocoin || row.refundRobocoin) }} / 补 {{ formatCoin(row.settlementExtraRobocoin) }}</small>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="130">
              <template #default="{ row }">
                <el-tag :type="orderTag(row.orderStatus)">{{ orderStatusLabel(row.orderStatus) }}</el-tag>
                <small v-if="row.executionMode === 'SUBCONTRACT'">{{ subcontractStatusLabel(row.subcontractStatus) }}</small>
                <small v-if="row.latestReviewTitle || row.latestDecisionTitle">{{ orderReviewTitle(row) }}</small>
              </template>
            </el-table-column>
            <el-table-column prop="updateTime" label="更新时间" min-width="170"></el-table-column>
            <el-table-column label="操作" width="260" fixed="right">
              <template #default="{ row }">
                <button
                  v-if="canConfirmSubcontract(row)"
                  class="table-receipt-btn subcontract-confirm"
                  type="button"
                  @click="confirmSubcontract(row)"
                >
                  确认协作
                </button>
                <button
                  v-if="canConfirmSubcontract(row)"
                  class="table-delete-btn"
                  type="button"
                  @click="rejectSubcontract(row)"
                >
                  拒绝
                </button>
                <button
                  class="table-receipt-btn"
                  type="button"
                  @click="openReceipt(row)"
                >
                  凭证
                </button>
                <button
                  class="table-receipt-btn secondary"
                  type="button"
                  @click="openOrderLogs(row)"
                >
                  日志
                </button>
                <button
                  class="table-delete-btn"
                  type="button"
                  :disabled="deletingOrderId === row.id || !canDeleteOrder(row)"
                  @click="deleteOrder(row)"
                >
                  {{ deletingOrderId === row.id ? '删除中' : '删除' }}
                </button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div v-if="activeTab === 'published'" class="panel-block">
          <div class="panel-head">
            <div>
              <p class="eyebrow">任务发布记录</p>
              <h2>已发布任务</h2>
            </div>
            <div class="filter-row">
              <button class="primary-btn" type="button" @click="$router.push({ name: 'mall' })">发布新任务</button>
              <button class="ghost-btn" type="button" @click="fetchPublishedTasks">刷新</button>
            </div>
          </div>

          <div class="metric-grid published-metric-grid">
            <article><span>全部发布</span><strong>{{ publishedTasks.length }}</strong></article>
            <article><span>可接单</span><strong>{{ publishedOpenTaskCount }}</strong></article>
            <article><span>待审核</span><strong>{{ publishedTaskCount('PENDING_REVIEW') }}</strong></article>
            <article><span>已接单</span><strong>{{ publishedDispatchCount('ACCEPTED') }}</strong></article>
          </div>

          <el-table v-loading="publishedTasksLoading" :data="publishedTasks" class="profile-table">
            <el-table-column prop="id" label="任务ID" width="90"></el-table-column>
            <el-table-column label="任务" min-width="240">
              <template #default="{ row }">
                <strong>{{ row.title || row.taskName || '未命名任务' }}</strong>
                <small>{{ serviceTypeLabel(row.serviceType) }} · {{ robotTypeLabel(row.expectedRobotType) }}</small>
              </template>
            </el-table-column>
            <el-table-column label="地点/时间" min-width="250">
              <template #default="{ row }">
                <span>{{ publishedTaskLocation(row) }}</span>
                <small>{{ row.scheduleTime || row.createTime || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="预算" min-width="130">
              <template #default="{ row }">
                <span>{{ formatCoin(row.estimatedRobocoin) }} ROC</span>
                <small>{{ row.prepayStatus || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="状态" min-width="170">
              <template #default="{ row }">
                <el-tag :type="publishedStatusTag(row)">{{ publishedStatusLabel(row) }}</el-tag>
                <small>{{ row.dispatchStatusText || publishedDispatchLabel(row.dispatchStatus) }}</small>
              </template>
            </el-table-column>
            <el-table-column prop="updateTime" label="更新时间" min-width="170"></el-table-column>
            <el-table-column label="操作" width="230" fixed="right">
              <template #default="{ row }">
                <div class="published-task-actions">
                <el-button v-if="canRepublishPublishedTask(row)" type="text" :disabled="republishingTaskId === row.id" @click="republishPublishedTaskRow(row)">
                  {{ republishingTaskId === row.id ? '重新发布中' : '重新发布' }}
                </el-button>
                <el-button
                  type="text"
                  class="danger-text"
                  :disabled="cancelingPublishedTaskId === row.id || !canCancelPublishedTask(row)"
                  @click="cancelPublishedTaskRow(row)"
                >
                  {{ cancelingPublishedTaskId === row.id ? '取消中' : '取消发布' }}
                </el-button>
                <el-button v-if="canDeletePublishedTask(row)" type="text" class="danger-text" :disabled="deletingPublishedTaskId === row.id" @click="deletePublishedTaskRow(row)">
                  {{ deletingPublishedTaskId === row.id ? '删除中' : '删除' }}
                </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-if="!publishedTasksLoading && publishedTasks.length === 0" description="暂无已发布任务"></el-empty>
        </div>

        <div v-if="activeTab === 'reviews'" class="panel-block">
          <div class="panel-head">
            <div>
              <p class="eyebrow">复核与仲裁</p>
              <h2>复核/仲裁</h2>
            </div>
            <div class="filter-row">
              <el-select v-model="reviewFilters.reviewStatus" clearable placeholder="处理状态" @change="fetchReviews">
                <el-option label="待处理" value="PENDING"></el-option>
                <el-option label="处理中" value="PROCESSING"></el-option>
                <el-option label="已处理" value="RESOLVED"></el-option>
                <el-option label="已驳回" value="REJECTED"></el-option>
              </el-select>
              <el-select v-model="reviewFilters.eventType" clearable placeholder="事件类型" @change="fetchReviews">
                <el-option label="异常复核" value="ALERT_REVIEW"></el-option>
                <el-option label="人工仲裁" value="ARBITRATION"></el-option>
                <el-option label="处理结果" value="REVIEW_RESULT"></el-option>
                <el-option label="仲裁结果" value="ARBITRATION_RESULT"></el-option>
              </el-select>
            </div>
          </div>

          <div class="metric-grid review-metric-grid">
            <article><span>全部记录</span><strong>{{ reviewSummary.totalReviews || 0 }}</strong></article>
            <article><span>待处理</span><strong>{{ reviewSummary.pendingReviews || 0 }}</strong></article>
            <article><span>已闭环</span><strong>{{ reviewSummary.resolvedReviews || 0 }}</strong></article>
            <article><span>仲裁记录</span><strong>{{ reviewSummary.arbitrationReviews || 0 }}</strong></article>
            <article><span>高风险</span><strong>{{ reviewSummary.dangerReviews || 0 }}</strong></article>
          </div>

          <section class="review-list" v-loading="reviewsLoading">
            <article v-for="item in reviews" :key="item.id" class="review-card" :class="item.eventLevel">
              <div class="review-card-head">
                <div>
                  <span>{{ eventTypeLabel(item.eventType) }} · {{ myRoleLabel(item.myRole) }}</span>
                  <h3>{{ item.eventTitle || '复核/仲裁记录' }}</h3>
                  <p>{{ item.taskTitle || '未命名任务' }} · 订单 #{{ item.orderId || '-' }} · {{ serviceTypeLabel(item.serviceType) }}</p>
                </div>
                <el-tag :type="reviewTag(item.reviewStatus)">{{ reviewStatusLabel(item.reviewStatus) }}</el-tag>
              </div>
              <p class="review-content">{{ item.eventContent || item.reviewNote || item.latestDecisionNote || '暂无处理说明。' }}</p>
              <div class="review-proof-grid">
                <span><em>机器人</em><b>{{ item.robotName || item.robotId || '-' }}</b></span>
                <span><em>证明类型</em><b>{{ proofTypeLabel(item.proofType) }}</b></span>
                <span><em>证明状态</em><b>{{ proofStatusLabel(item.proofStatus) }}</b></span>
                <span><em>提交时间</em><b>{{ item.createTime || '-' }}</b></span>
              </div>
              <div v-if="item.latestDecisionTitle || item.latestProofDigest || item.proofDigest" class="review-decision">
                <strong>{{ item.latestDecisionTitle || '可信证据摘要' }}</strong>
                <p>{{ item.latestDecisionNote || item.latestProofDigest || item.proofDigest }}</p>
              </div>
              <div class="review-actions-row">
                <button class="table-receipt-btn secondary" type="button" @click="openOrderLogs(reviewLogOrder(item))">查看订单日志</button>
                <button class="table-receipt-btn" type="button" @click="copyReviewText(item)">复制复核信息</button>
              </div>
            </article>
            <el-empty v-if="!reviewsLoading && reviews.length === 0" description="暂无复核/仲裁记录"></el-empty>
          </section>
        </div>

        <div v-if="activeTab === 'wallet'" class="panel-block">
          <div class="panel-head">
            <div>
              <p class="eyebrow">ROBOT WALLET</p>
              <h2>机器人钱包</h2>
            </div>
            <button class="primary-btn" @click="$router.push({ name: 'user' })">注册新机器人</button>
          </div>
          <section class="chain-freeze-panel">
            <div>
              <p class="eyebrow">CHAIN ASSET PLACEHOLDER</p>
              <h3>链上冻结资产</h3>
              <span>当前为平台补助阶段，暂不从用户钱包扣款；后续接入链上钱包后，这里会显示冻结、划转、退款和交易哈希。</span>
            </div>
            <div class="freeze-metrics">
              <article><small>机器人钱包总额</small><strong>{{ formatCoin(totalRobotCoin) }}</strong><em>RoboCoin</em></article>
              <article><small>链上冻结中</small><strong>{{ formatCoin(frozenAssetAmount) }}</strong><em>待接入</em></article>
              <article><small>平台补助中</small><strong>{{ formatCoin(platformSubsidyAmount) }}</strong><em>当前阶段</em></article>
              <article><small>待结算</small><strong>{{ formatCoin(pendingSettlementAmount) }}</strong><em>订单完成后划转</em></article>
            </div>
          </section>
          <div class="wallet-grid">
            <article v-for="robot in robots" :key="robot.id" class="wallet-card">
              <img v-if="robot.coverImageUrl" :src="robot.coverImageUrl" :alt="robot.name" />
              <div v-else class="wallet-avatar">ROC</div>
              <div>
                <span>{{ robotTypeLabel(robot.category) }}</span>
                <h3>{{ robot.name }}</h3>
                <p>{{ shortenWallet(robot.walAddress) }}</p>
                <p>地域限制：{{ robot.serviceRegionLimit || '不限制' }}</p>
                <button
                  class="wallet-archive-link"
                  type="button"
                  @click="$router.push({ name: 'robotArchive', params: { robotId: robot.id } })"
                >机器人档案库</button>
              </div>
              <strong>{{ formatCoin(robot.money) }} RoboCoin</strong>
              <em>{{ sdkStatusLabel(robot) }}</em>
            </article>
            <el-empty v-if="!robotsLoading && robots.length === 0" description="暂无机器人钱包"></el-empty>
          </div>
        </div>

        <div v-if="activeTab === 'profile'" class="panel-block">
          <div class="panel-head">
            <div>
              <p class="eyebrow">PROFILE</p>
              <h2>账号资料</h2>
            </div>
          </div>
          <el-form label-position="top" class="profile-form">
            <el-form-item label="昵称">
              <el-input v-model.trim="profileForm.username"></el-input>
            </el-form-item>
            <el-form-item label="邮箱">
              <el-input v-model.trim="profileForm.email"></el-input>
            </el-form-item>
            <div class="form-grid">
              <el-form-item label="国际区号">
                <el-input v-model.trim="profileForm.phoneCountryCode" placeholder="+86"></el-input>
              </el-form-item>
              <el-form-item label="手机号">
                <el-input v-model.trim="profileForm.phone"></el-input>
              </el-form-item>
            </div>
            <button class="primary-btn" type="button" :disabled="savingProfile" @click="saveProfile">
              {{ savingProfile ? '保存中...' : '保存资料' }}
            </button>
          </el-form>
        </div>

        <div v-if="activeTab === 'security'" class="panel-block">
          <div class="panel-head">
            <div>
              <p class="eyebrow">SECURITY</p>
              <h2>账号安全</h2>
            </div>
          </div>
          <el-form label-position="top" class="profile-form">
            <el-form-item label="当前密码">
              <el-input v-model="passwordForm.oldPassword" type="password" show-password></el-input>
            </el-form-item>
            <el-form-item label="新密码">
              <el-input v-model="passwordForm.newPassword" type="password" show-password></el-input>
            </el-form-item>
            <el-form-item label="确认新密码">
              <el-input v-model="passwordForm.confirmPassword" type="password" show-password></el-input>
            </el-form-item>
            <button class="primary-btn" type="button" :disabled="savingPassword" @click="savePassword">
              {{ savingPassword ? '修改中...' : '修改密码' }}
            </button>
          </el-form>
        </div>
      </section>
    </main>

    <el-dialog
      v-model="receiptVisible"
      width="720px"
      custom-class="receipt-dialog"
      append-to-body
    >
      <div v-if="receiptOrder" class="receipt-panel">
        <div class="receipt-head">
          <div>
            <p class="eyebrow">ROBOCOIN SETTLEMENT RECEIPT</p>
            <h2>{{ receiptOrder.taskTitle || '订单结算凭证' }}</h2>
            <span>凭证编号 {{ receiptNo(receiptOrder) }} · 订单 #{{ receiptOrder.id }} · {{ serviceTypeLabel(receiptOrder.serviceType) }} · {{ orderStatusLabel(receiptOrder.orderStatus) }}</span>
          </div>
          <el-tag :type="orderTag(receiptOrder.orderStatus)">{{ settlementReceiptStatus(receiptOrder) }}</el-tag>
        </div>
        <div class="receipt-action-row">
          <button type="button" class="table-receipt-btn" @click="copyReceiptNo(receiptOrder)">复制编号</button>
          <button type="button" class="table-receipt-btn secondary" @click="copyReceiptText(receiptOrder)">复制凭证信息</button>
          <button type="button" class="table-receipt-btn secondary" @click="printReceipt">打印凭证</button>
        </div>

        <div class="receipt-grid">
          <article><span>平台补助</span><strong>{{ formatCoin(receiptOrder.prepaidRobocoin) }} ROC</strong></article>
          <article><span>最终应付</span><strong>{{ formatCoin(receiptOrder.finalRobocoin) }} ROC</strong></article>
          <article><span>平台手续费</span><strong>{{ formatCoin(platformFeeAmount(receiptOrder)) }} ROC</strong></article>
          <article><span>服务方收入</span><strong>{{ formatCoin(providerIncomeAmount(receiptOrder)) }} ROC</strong></article>
          <article><span>退回</span><strong>{{ formatCoin(receiptOrder.settlementRefundRobocoin || receiptOrder.refundRobocoin) }} ROC</strong></article>
          <article><span>补扣</span><strong>{{ formatCoin(receiptOrder.settlementExtraRobocoin) }} ROC</strong></article>
        </div>

        <section class="receipt-section">
          <strong>链上资产占位</strong>
          <p>当前阶段由平台提供任务补助，暂不执行真实链上冻结。后续接入用户钱包后，本凭证会展示冻结交易、划转交易、退款交易和链上哈希。</p>
        </section>

        <section class="receipt-section">
          <strong>履约摘要</strong>
          <p>{{ receiptOrder.settlementNote || receiptOrder.derasNote || '暂无详细结算说明。' }}</p>
          <div class="receipt-meta">
            <span>开始：{{ receiptOrder.serviceStartTime || '未记录' }}</span>
            <span>结束：{{ receiptOrder.serviceEndTime || '未记录' }}</span>
            <span>服务时长：{{ receiptServiceTime(receiptOrder) }}</span>
          </div>
        </section>

        <section v-if="hasReviewOrProof(receiptOrder)" class="receipt-section review-receipt-section">
          <strong>仲裁/复核结果</strong>
          <p>{{ receiptOrder.latestDecisionNote || receiptOrder.latestReviewNote || receiptOrder.arbitrationReason || '暂无裁决说明。' }}</p>
          <div class="receipt-meta">
            <span>{{ orderReviewTitle(receiptOrder) }}</span>
            <span>{{ reviewStatusLabel(receiptOrder.latestReviewStatus || receiptOrder.arbitrationStatus) }}</span>
            <span>日志 {{ receiptOrder.fulfillmentLogCount || 0 }} 条</span>
          </div>
        </section>

        <section v-if="receiptOrder.latestProofDigest" class="receipt-section">
          <strong>可信证据摘要</strong>
          <p class="receipt-digest">{{ receiptOrder.latestProofDigest }}</p>
          <div class="receipt-meta">
            <span>{{ proofTypeLabel(receiptOrder.latestProofType) }}</span>
            <span>{{ proofStatusLabel(receiptOrder.latestProofStatus) }}</span>
          </div>
        </section>
      </div>
    </el-dialog>

    <el-dialog
      v-model="logVisible"
      width="760px"
      title="订单履约日志"
      custom-class="order-log-dialog"
      append-to-body
    >
      <div v-if="logOrder" class="order-log-panel" v-loading="logsLoading">
        <header>
          <p class="eyebrow">FULFILLMENT LOG</p>
          <h2>{{ logOrder.taskTitle || '订单履约日志' }}</h2>
          <span>订单 #{{ logOrder.id }} · {{ orderStatusLabel(logOrder.orderStatus) }} · {{ orderReviewTitle(logOrder) }}</span>
        </header>
        <div class="order-log-list">
          <article v-for="log in orderLogs" :key="log.id" :class="log.eventLevel">
            <i></i>
            <div>
              <span>{{ log.createTime || '-' }} · {{ eventTypeLabel(log.eventType) }} · {{ proofTypeLabel(log.proofType) }}</span>
              <strong>{{ log.eventTitle || '履约记录' }}</strong>
              <p>{{ log.eventContent || log.reviewNote || log.proofDigest || '-' }}</p>
              <em v-if="log.proofDigest">{{ log.proofDigest }}</em>
            </div>
          </article>
          <el-empty v-if="!logsLoading && orderLogs.length === 0" description="暂无履约日志"></el-empty>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { changeUserPassword, getUserProfile, updateUserProfile } from '../api/auth'
import { confirmSubcontractPartner, deleteMyOrder, getMyOrders, getMyOrderReviews, getOrderFulfillmentLogs, rejectSubcontractPartner } from '../api/orders'
import { getMyRegisteredRobots } from '../api/robot-register'
import { cancelPublishedTask, deletePublishedTask, getMyPublishedTasks, republishCancelledTask } from '../api/task-publish'
import { getAuthUser, setAuthUser } from '../utils/auth'

const AVATAR_KEY_PREFIX = 'roc-profile-avatar-'

export default {
  name: 'UserProfile',
  data() {
    return {
      currentUser: getAuthUser() || {},
      activeTab: 'orders',
      ordersLoading: false,
      publishedTasksLoading: false,
      robotsLoading: false,
      reviewsLoading: false,
      savingProfile: false,
      savingPassword: false,
      deletingOrderId: null,
      cancelingPublishedTaskId: null,
      deletingPublishedTaskId: null,
      republishingTaskId: null,
      orders: [],
      publishedTasks: [],
      robots: [],
      reviews: [],
      orderSummary: {},
      reviewSummary: {},
      avatarUrl: '',
      receiptVisible: false,
      receiptOrder: null,
      logVisible: false,
      logOrder: null,
      logsLoading: false,
      orderLogs: [],
      orderFilters: {
        status: '',
        role: ''
      },
      reviewFilters: {
        reviewStatus: '',
        eventType: ''
      },
      profileForm: {
        username: '',
        email: '',
        phoneCountryCode: '',
        phone: '',
        reputationScore: 80
      },
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      tabs: [
        { code: '01', key: 'orders', label: '全部订单', desc: '已接单和服务历史' },
        { code: '02', key: 'published', label: '已发布任务', desc: '我创建的任务需求' },
        { code: '03', key: 'reviews', label: '复核/仲裁', desc: '异常与处理结果' },
        { code: '04', key: 'wallet', label: '机器人钱包', desc: 'RoboCoin余额' },
        { code: '05', key: 'profile', label: '账号资料', desc: '头像、姓名、联系方式' },
        { code: '06', key: 'security', label: '账号安全', desc: '修改登录密码' }
      ]
    }
  },
  computed: {
    avatarLetter() {
      const name = this.profileForm.username || this.currentUser.username || 'R'
      return name.slice(0, 1).toUpperCase()
    },
    totalRobotCoin() {
      return this.robots.reduce((sum, robot) => sum + Number(robot.money || 0), 0)
    },
    platformSubsidyAmount() {
      return this.orders.reduce((sum, order) => {
        if (['ACCEPTED', 'RUNNING', 'ARBITRATION'].includes(order.orderStatus)) {
          return sum + Number(order.prepaidRobocoin || 0)
        }
        return sum
      }, 0)
    },
    pendingSettlementAmount() {
      return this.orders.reduce((sum, order) => {
        if (['ACCEPTED', 'RUNNING', 'ARBITRATION'].includes(order.orderStatus)) {
          return sum + Number(order.finalRobocoin || order.prepaidRobocoin || 0)
        }
        return sum
      }, 0)
    },
    frozenAssetAmount() {
      return 0
    },
    publishedOpenTaskCount() {
      return this.publishedTasks.filter(task => this.isPublishedTaskOpen(task)).length
    }
  },
  created() {
    this.avatarUrl = localStorage.getItem(`${AVATAR_KEY_PREFIX}${this.currentUser.id}`) || ''
    this.profileForm = {
      ...this.profileForm,
      ...this.currentUser,
      phone: this.currentUser.phone || ''
    }
    this.refreshAll()
  },
  methods: {
    refreshAll() {
      this.fetchProfile()
      this.fetchOrders()
      this.fetchPublishedTasks()
      this.fetchReviews()
      this.fetchRobots()
    },
    async fetchProfile() {
      if (!this.currentUser.id) return
      try {
        const response = await getUserProfile(this.currentUser.id)
        if (response.data.code === 200 && response.data.data) {
          const data = response.data.data
          this.profileForm = {
            username: data.username || '',
            email: data.email || '',
            phoneCountryCode: data.phoneCountryCode || '',
            phone: data.phone || '',
            reputationScore: data.reputationScore || 80
          }
        }
      } catch (error) {
        this.$message.error('个人资料加载失败')
      }
    },
    async fetchOrders() {
      if (!this.currentUser.id) return
      this.ordersLoading = true
      try {
        const response = await getMyOrders({
          userId: this.currentUser.id,
          orderStatus: this.orderFilters.status || undefined,
          role: this.orderFilters.role || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '订单加载失败')
          return
        }
        const data = response.data.data || {}
        this.orders = data.records || []
        this.orderSummary = data.summary || {}
      } catch (error) {
        this.$message.error('订单请求失败')
      } finally {
        this.ordersLoading = false
      }
    },
    async fetchPublishedTasks() {
      if (!this.currentUser.id) return
      this.publishedTasksLoading = true
      try {
        const response = await getMyPublishedTasks(this.currentUser.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '已发布任务加载失败')
          return
        }
        this.publishedTasks = response.data.data || []
      } catch (error) {
        this.$message.error('已发布任务请求失败')
      } finally {
        this.publishedTasksLoading = false
      }
    },
    async fetchReviews() {
      if (!this.currentUser.id) return
      this.reviewsLoading = true
      try {
        const response = await getMyOrderReviews({
          userId: this.currentUser.id,
          reviewStatus: this.reviewFilters.reviewStatus || undefined,
          eventType: this.reviewFilters.eventType || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '复核/仲裁记录加载失败')
          return
        }
        const data = response.data.data || {}
        this.reviews = data.records || []
        this.reviewSummary = data.summary || {}
      } catch (error) {
        this.$message.error('复核/仲裁记录请求失败')
      } finally {
        this.reviewsLoading = false
      }
    },
    async fetchRobots() {
      if (!this.currentUser.id) return
      this.robotsLoading = true
      try {
        const response = await getMyRegisteredRobots(this.currentUser.id)
        if (response.data.code === 200) {
          this.robots = (response.data.data || []).filter(robot => robot.registerStatus !== 'CANCELLED')
        }
      } catch (error) {
        this.$message.error('机器人钱包加载失败')
      } finally {
        this.robotsLoading = false
      }
    },
    async saveProfile() {
      this.savingProfile = true
      try {
        const response = await updateUserProfile({
          userId: this.currentUser.id,
          username: this.profileForm.username,
          email: this.profileForm.email,
          phoneCountryCode: this.profileForm.phoneCountryCode,
          phone: this.profileForm.phone
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '资料保存失败')
          return
        }
        const authUser = response.data.data && response.data.data.authUser
        if (authUser) {
          setAuthUser(authUser)
          this.currentUser = authUser
        }
        this.$message.success('个人资料已保存')
      } catch (error) {
        this.$message.error('资料保存请求失败')
      } finally {
        this.savingProfile = false
      }
    },
    async savePassword() {
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.$message.warning('两次输入的新密码不一致')
        return
      }
      this.savingPassword = true
      try {
        const response = await changeUserPassword({
          userId: this.currentUser.id,
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '密码修改失败')
          return
        }
        this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        this.$message.success('密码已修改')
      } catch (error) {
        this.$message.error('密码修改请求失败')
      } finally {
        this.savingPassword = false
      }
    },
    handleAvatarChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.$message.warning('请选择图片文件')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        this.avatarUrl = reader.result
        localStorage.setItem(`${AVATAR_KEY_PREFIX}${this.currentUser.id}`, this.avatarUrl)
      }
      reader.readAsDataURL(file)
    },
    canDeleteOrder(row) {
      return row && !['ACCEPTED', 'RUNNING'].includes(row.orderStatus)
    },
    canConfirmSubcontract(row) {
      return row && row.executionMode === 'SUBCONTRACT' && row.subcontractStatus === 'PARTNER_PENDING' && Number(row.secondaryTakerUserId) === Number(this.currentUser.id)
    },
    async confirmSubcontract(row) {
      try {
        await this.$confirm('确认后两台机器人、交接点和分账比例都不能中途修改。', '确认两段协作', {
          confirmButtonText: '确认协作',
          cancelButtonText: '稍后处理',
          type: 'warning'
        })
        const response = await confirmSubcontractPartner(row.id, { userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '协作确认失败')
          return
        }
        this.$message.success('协作方案已生效，可以开始履约')
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('协作确认失败')
      }
    },
    async rejectSubcontract(row) {
      try {
        const { value } = await this.$prompt('拒绝后整份分包方案会取消，原任务将重新开放派单。请输入原因。', '拒绝协作', {
          confirmButtonText: '确认拒绝',
          cancelButtonText: '返回',
          inputPattern: /^.{2,255}$/,
          inputErrorMessage: '请输入 2 至 255 个字符'
        })
        const response = await rejectSubcontractPartner(row.id, { userId: this.currentUser.id, reason: value })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '拒绝协作失败')
          return
        }
        this.$message.success('分包方案已取消，任务已重新开放')
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('拒绝协作失败')
      }
    },
    openReceipt(row) {
      this.receiptOrder = row
      this.receiptVisible = true
    },
    async openOrderLogs(row) {
      if (!row || !this.currentUser.id) return
      this.logOrder = row
      this.orderLogs = []
      this.logVisible = true
      this.logsLoading = true
      try {
        const response = await getOrderFulfillmentLogs(row.id, this.currentUser.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '履约日志加载失败')
          return
        }
        this.orderLogs = response.data.data || []
      } catch (error) {
        this.$message.error('履约日志请求失败')
      } finally {
        this.logsLoading = false
      }
    },
    receiptNo(order) {
      if (!order) return 'ROC-ORDER-UNKNOWN'
      const rawDate = order.serviceEndTime || order.updateTime || order.createTime || new Date()
      const date = new Date(rawDate)
      const yyyy = Number.isFinite(date.getTime()) ? date.getFullYear() : new Date().getFullYear()
      const mm = String(Number.isFinite(date.getTime()) ? date.getMonth() + 1 : new Date().getMonth() + 1).padStart(2, '0')
      const dd = String(Number.isFinite(date.getTime()) ? date.getDate() : new Date().getDate()).padStart(2, '0')
      return `ROC-ORDER-${yyyy}${mm}${dd}-${String(order.id || 0).padStart(6, '0')}`
    },
    async copyToClipboard(text, successText) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          input.setAttribute('readonly', 'readonly')
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$message.success(successText)
      } catch (error) {
        this.$message.error('复制失败，请手动复制')
      }
    },
    copyReceiptNo(order) {
      this.copyToClipboard(this.receiptNo(order), '凭证编号已复制')
    },
    copyReceiptText(order) {
      if (!order) return
      const lines = [
        `凭证编号：${this.receiptNo(order)}`,
        `订单：#${order.id} ${order.taskTitle || ''}`,
        `服务类型：${this.serviceTypeLabel(order.serviceType)}`,
        `状态：${this.orderStatusLabel(order.orderStatus)}`,
        `平台补助：${this.formatCoin(order.prepaidRobocoin)} ROC`,
        `最终应付：${this.formatCoin(order.finalRobocoin)} ROC`,
        `平台手续费：${this.formatCoin(this.platformFeeAmount(order))} ROC`,
        `服务方收入：${this.formatCoin(this.providerIncomeAmount(order))} ROC`,
        `服务时长：${this.receiptServiceTime(order)}`,
        `复核/仲裁：${this.orderReviewTitle(order)}`,
        `可信证据：${order.latestProofDigest || '暂无'}`
      ]
      this.copyToClipboard(lines.join('\n'), '凭证信息已复制')
    },
    reviewLogOrder(item) {
      return {
        id: item.orderId,
        taskTitle: item.taskTitle,
        orderStatus: item.orderStatus,
        latestReviewTitle: item.eventTitle,
        latestDecisionTitle: item.latestDecisionTitle,
        serviceType: item.serviceType
      }
    },
    copyReviewText(item) {
      if (!item) return
      const lines = [
        `复核记录：#${item.id}`,
        `订单：#${item.orderId || '-'}`,
        `任务：${item.taskTitle || '未命名任务'}`,
        `我的身份：${this.myRoleLabel(item.myRole)}`,
        `事件类型：${this.eventTypeLabel(item.eventType)}`,
        `处理状态：${this.reviewStatusLabel(item.reviewStatus)}`,
        `事件说明：${item.eventContent || item.reviewNote || '-'}`,
        `裁决结果：${item.latestDecisionTitle || item.latestDecisionNote || '暂无'}`,
        `证据摘要：${item.latestProofDigest || item.proofDigest || '暂无'}`
      ]
      this.copyToClipboard(lines.join('\n'), '复核信息已复制')
    },
    printReceipt() {
      window.print()
    },
    async deleteOrder(row) {
      if (!row || !this.currentUser.id) return
      if (!this.canDeleteOrder(row)) {
        this.$message.warning('进行中的订单不能删除，请先处理完成或取消')
        return
      }
      try {
        await this.$confirm('确认从个人中心删除这条订单记录吗？平台后台和财务审计记录会继续保留。', '删除订单', {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
        this.deletingOrderId = row.id
        const response = await deleteMyOrder(row.id, this.currentUser.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '订单删除失败')
          return
        }
        this.$message.success('订单已从个人中心删除')
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('订单删除失败')
        }
      } finally {
        this.deletingOrderId = null
      }
    },
    formatCoin(value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },
    publishedTaskCount(status) {
      return this.publishedTasks.filter(task => String(task.publishStatus || '').toUpperCase() === status).length
    },
    publishedDispatchCount(status) {
      return this.publishedTasks.filter(task => String(task.dispatchStatus || '').toUpperCase() === status).length
    },
    isPublishedTaskAccepted(task) {
      if (!task) return false
      return Number(task.status) === 1 || String(task.dispatchStatus || '').toUpperCase() === 'ACCEPTED'
    },
    isPublishedTaskOpen(task) {
      if (!task) return false
      return String(task.publishStatus || '').toUpperCase() === 'ACTIVE' && !this.isPublishedTaskAccepted(task)
    },
    canCancelPublishedTask(task) {
      if (!task) return false
      return !['CANCELLED', 'FINISHED', 'REJECTED'].includes(String(task.publishStatus || '').toUpperCase())
    },
    canRepublishPublishedTask(task) {
      if (!task || this.isPublishedTaskAccepted(task)) return false
      return String(task.publishStatus || '').toUpperCase() === 'CANCELLED'
    },
    canDeletePublishedTask(task) {
      return this.canRepublishPublishedTask(task)
    },
    async republishPublishedTaskRow(row) {
      if (!row || !this.currentUser.id) return
      try {
        await this.$confirm('重新发布后，任务会恢复并再次进入 DeRAS 审核与派单流程。', '重新发布任务', {
          confirmButtonText: '重新发布',
          cancelButtonText: '保持取消',
          type: 'warning'
        })
        this.republishingTaskId = row.id
        const response = await republishCancelledTask({ taskId: row.id, userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务重新发布失败')
          return
        }
        this.$message.success(response.data.msg || '任务已重新发布并进入审核队列')
        this.fetchPublishedTasks()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('任务重新发布失败')
      } finally {
        this.republishingTaskId = null
      }
    },
    async deletePublishedTaskRow(row) {
      if (!row || !this.currentUser.id) return
      try {
        await this.$confirm('该操作会从已发布任务列表中永久删除这条已取消任务。', '删除任务', {
          confirmButtonText: '永久删除',
          cancelButtonText: '保留任务',
          type: 'warning'
        })
        this.deletingPublishedTaskId = row.id
        const response = await deletePublishedTask({ taskId: row.id, userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务删除失败')
          return
        }
        this.$message.success('任务已删除')
        this.fetchPublishedTasks()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('任务删除失败')
      } finally {
        this.deletingPublishedTaskId = null
      }
    },
    publishedStatusLabel(task) {
      const value = task && task.publishStatus ? String(task.publishStatus).toUpperCase() : ''
      if (this.isPublishedTaskAccepted(task)) return '已接单'
      const map = {
        PENDING_REVIEW: '待审核',
        ACTIVE: '可接单',
        ACCEPTED: '已接单',
        CANCELLED: '已取消',
        FINISHED: '已完成',
        REJECTED: '已驳回'
      }
      return map[value] || value || '未知'
    },
    publishedStatusTag(task) {
      const value = task && task.publishStatus ? String(task.publishStatus).toUpperCase() : ''
      if (this.isPublishedTaskAccepted(task)) return 'warning'
      if (value === 'ACTIVE') return 'success'
      if (value === 'PENDING_REVIEW') return 'warning'
      if (value === 'CANCELLED' || value === 'REJECTED') return 'info'
      return ''
    },
    async cancelPublishedTaskRow(row) {
      if (!row || !this.currentUser.id) return
      try {
        await this.$confirm('确认取消这个任务发布吗？如果已经生成订单，系统会同步取消订单并释放机器人。', '取消发布', {
          confirmButtonText: '确认取消',
          cancelButtonText: '先不取消',
          type: 'warning'
        })
        this.cancelingPublishedTaskId = row.id
        const response = await cancelPublishedTask({ taskId: row.id, userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务取消失败')
          return
        }
        this.$message.success(response.data.msg || '任务已取消')
        this.fetchPublishedTasks()
        this.fetchOrders()
        this.fetchRobots()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('任务取消失败')
        }
      } finally {
        this.cancelingPublishedTaskId = null
      }
    },
    publishedDispatchLabel(value) {
      const map = {
        ACCEPTED: '任务已接单',
        OPEN_GRAB: '开放抢单',
        NO_CANDIDATE: '暂无候选',
        PENDING: '等待派单'
      }
      return map[value] || value || '等待派单'
    },
    publishedTaskLocation(row) {
      if (!row) return '-'
      if (row.serviceType === 'SHUTTLE') return `${row.beginAddress || '未填写起点'} → ${row.endAddress || '未填写终点'}`
      return row.serviceAddress || '未填写服务地点'
    },
    serviceTypeLabel(value) {
      if (value === 'SHUTTLE') return '接驳服务'
      if (value === 'HOME_SERVICE') return '上门服务'
      return value || '未知服务'
    },
    robotTypeLabel(value) {
      const map = {
        CLEANING: '清洁机器人',
        DELIVERY: '配送机器人',
        SHUTTLE: '接驳机器人',
        INSPECTION: '巡检机器人'
      }
      return map[value] || value || '未分类'
    },
    roleLabel(row) {
      if (Number(row.publisherUserId) === Number(this.currentUser.id)) return '我是发布者'
      if (Number(row.takerUserId) === Number(this.currentUser.id)) return '我是服务者'
      if (Number(row.secondaryTakerUserId) === Number(this.currentUser.id)) return '我是第二段协作方'
      return '相关订单'
    },
    subcontractStatusLabel(value) {
      const map = {
        PARTNER_PENDING: '等待第二段机器人确认',
        READY: '两段方案已锁定',
        HANDOVER_PENDING: '等待交接核验',
        COMPLETED: '两段协作已完成',
        CANCELLED: '分包方案已取消'
      }
      return map[value] || value || '整单执行'
    },
    myRoleLabel(value) {
      if (value === 'PUBLISHER') return '我是发布者'
      if (value === 'TAKER') return '我是服务者'
      return '相关方'
    },
    orderStatusLabel(value) {
      const map = {
        ACCEPTED: '已接单',
        RUNNING: '进行中',
        FINISHED: '已完成',
        CANCELLED: '已取消',
        ARBITRATION: '仲裁中'
      }
      return map[value] || value || '未知'
    },
    orderTag(value) {
      if (value === 'FINISHED') return 'success'
      if (value === 'RUNNING' || value === 'ACCEPTED') return 'warning'
      if (value === 'CANCELLED') return 'info'
      if (value === 'ARBITRATION') return 'danger'
      return ''
    },
    platformFeeAmount(order) {
      if (!order) return 0
      if (order.platformFeeRobocoin !== null && order.platformFeeRobocoin !== undefined) return order.platformFeeRobocoin
      return Number(order.finalRobocoin || 0) * 0.01
    },
    providerIncomeAmount(order) {
      if (!order) return 0
      if (order.providerIncomeRobocoin !== null && order.providerIncomeRobocoin !== undefined) return order.providerIncomeRobocoin
      return Math.max(0, Number(order.finalRobocoin || 0) - Number(this.platformFeeAmount(order) || 0))
    },
    settlementReceiptStatus(order) {
      if (!order) return '未生成'
      if (order.orderStatus === 'FINISHED') return '已生成结算凭证'
      if (order.orderStatus === 'CANCELLED') return '取消凭证'
      if (order.orderStatus === 'ARBITRATION') return '仲裁冻结中'
      return '服务处理中'
    },
    hasReviewOrProof(order) {
      return Boolean(order && (order.latestDecisionTitle || order.latestReviewTitle || order.arbitrationStatus || order.latestProofDigest))
    },
    orderReviewTitle(row) {
      if (!row) return '暂无复核'
      if (row.latestDecisionTitle) return row.latestDecisionTitle
      if (row.latestReviewTitle) return row.latestReviewTitle
      if (row.orderStatus === 'ARBITRATION') return '订单仲裁中'
      return '暂无复核'
    },
    reviewStatusLabel(value) {
      const map = {
        PENDING: '待处理',
        PROCESSING: '处理中',
        REVIEWING: '复核中',
        RESOLVED: '已处理',
        REJECTED: '已驳回'
      }
      return map[value] || value || '未记录'
    },
    reviewTag(value) {
      if (value === 'RESOLVED') return 'success'
      if (value === 'REJECTED') return 'info'
      if (value === 'PROCESSING' || value === 'REVIEWING') return 'warning'
      return 'danger'
    },
    proofTypeLabel(value) {
      const map = {
        TPM_SIGNATURE: 'TPM签名',
        GPS: 'GPS定位',
        USER_CONFIRM: '用户确认',
        DERAS_AUDIT: 'DeRAS审计',
        SETTLEMENT: '结算凭证',
        POT_OFFCHAIN: 'PoT链下证明',
        'TPM_SIGNATURE+POT_OFFCHAIN': 'TPM签名 + PoT证明'
      }
      return map[value] || value || '未记录'
    },
    proofStatusLabel(value) {
      const map = {
        PENDING: '待处理',
        VERIFIED: '已验证',
        REVIEWING: '复核中',
        RESOLVED: '已处理',
        REJECTED: '已驳回',
        FAILED: '失败'
      }
      return map[value] || value || '未记录'
    },
    eventTypeLabel(value) {
      const map = {
        WORKFLOW: '流程',
        ALERT_REVIEW: '异常复核',
        ARBITRATION: '人工仲裁',
        REVIEW_RESULT: '处理结果',
        ARBITRATION_RESULT: '仲裁结果',
        SDK_VERIFY: 'SDK验签',
        SDK_HEARTBEAT: 'SDK心跳',
        SETTLEMENT: '结算',
        CANCEL: '取消',
        POT_PROOF: 'PoT证明'
      }
      return map[value] || value || '履约记录'
    },
    receiptServiceTime(order) {
      if (!order) return '-'
      if (order.actualServiceMinutes) return `${order.actualServiceMinutes} 分钟`
      if (!order.serviceStartTime || !order.serviceEndTime) return '未完成计时'
      const start = new Date(order.serviceStartTime).getTime()
      const end = new Date(order.serviceEndTime).getTime()
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return '未完成计时'
      return `${Math.ceil((end - start) / 60000)} 分钟`
    },
    shortenWallet(value) {
      if (!value) return '未填写钱包地址'
      return value.length > 18 ? `${value.slice(0, 10)}...${value.slice(-8)}` : value
    },
    sdkStatusLabel(robot) {
      if (robot.sdkConfirmedAt) return 'SDK已确认'
      if (robot.sdkBoundAt) return 'SDK待确认'
      return robot.registerStatus || '待注册'
    }
  }
}
</script>

<style scoped>
.profile-page {
  position: relative;
  min-height: 100vh;
  padding: 30px;
  overflow: hidden;
  color: #fff6ea;
  background:
    radial-gradient(circle at 12% 8%, rgba(255, 183, 82, 0.16), transparent 26%),
    radial-gradient(circle at 88% 4%, rgba(79, 198, 255, 0.16), transparent 24%),
    linear-gradient(135deg, #06111e 0%, #0a1524 48%, #050b15 100%);
}

.profile-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 48px 48px;
  opacity: 0.44;
}

.profile-header,
.profile-layout {
  position: relative;
  z-index: 1;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 22px;
}

.eyebrow {
  margin: 0 0 10px;
  color: rgba(255, 205, 145, 0.86);
  letter-spacing: 0.26em;
  font-size: 12px;
}

.profile-header h1 {
  margin: 0;
  font-size: clamp(38px, 5vw, 66px);
  font-family: ui-serif, serif;
}

.profile-header p {
  max-width: 760px;
  margin: 12px 0 0;
  color: rgba(255, 246, 234, 0.7);
  line-height: 1.8;
}

.header-actions,
.filter-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ghost-btn,
.primary-btn {
  height: 42px;
  padding: 0 16px;
  border-radius: 999px;
  cursor: pointer;
}

.ghost-btn {
  color: #fff6ea;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
}

.primary-btn {
  border: none;
  color: #06111e;
  font-weight: 900;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.profile-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 18px;
}

.profile-card,
.content-panel {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 28px;
  background: rgba(8, 16, 28, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
}

.profile-card {
  padding: 22px;
  align-self: start;
}

.avatar-wrap {
  position: relative;
  width: 124px;
  height: 124px;
  margin-bottom: 18px;
}

.avatar-wrap img,
.avatar-wrap > span {
  width: 124px;
  height: 124px;
  display: grid;
  place-items: center;
  border-radius: 32px;
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
  font-size: 44px;
  font-weight: 900;
  object-fit: cover;
}

.avatar-wrap label {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: -10px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #06111e;
  background: #fff6ea;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}

.avatar-wrap input {
  display: none;
}

.profile-card h2 {
  margin: 0;
  font-size: 26px;
}

.profile-card p {
  margin: 8px 0 0;
  color: rgba(255, 246, 234, 0.62);
}

.profile-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 18px 0;
}

.profile-stats div,
.metric-grid article {
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(143, 241, 199, 0.14);
  background: rgba(255, 255, 255, 0.055);
}

.profile-stats span,
.metric-grid span {
  display: block;
  color: rgba(143, 241, 199, 0.85);
  font-size: 12px;
}

.profile-stats strong,
.metric-grid strong {
  display: block;
  margin-top: 8px;
  color: #fff;
  font-size: 22px;
}

.profile-menu {
  display: grid;
  gap: 10px;
}

.profile-menu button {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 12px;
  align-items: center;
  min-height: 62px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 246, 234, 0.78);
  text-align: left;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
}

.profile-menu button.active,
.profile-menu button:hover {
  color: #fff;
  border-color: rgba(143, 241, 199, 0.36);
  background: linear-gradient(135deg, rgba(143, 241, 199, 0.14), rgba(142, 215, 255, 0.08));
}

.profile-menu button > span {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #06111e;
  background: rgba(143, 241, 199, 0.88);
  font-weight: 900;
}

.profile-menu strong,
.profile-menu small {
  display: block;
}

.profile-menu small {
  margin-top: 4px;
  color: rgba(255, 246, 234, 0.48);
}

.content-panel {
  min-width: 0;
  padding: 22px;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.panel-head h2 {
  margin: 0;
  font-size: 30px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.profile-table {
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 8px;
  background: transparent;
}

.profile-table:deep(.el-table) {
  --el-table-bg-color: rgba(5, 13, 23, 0.76);
  --el-table-tr-bg-color: rgba(5, 13, 23, 0.76);
  --el-table-header-bg-color: #0b1c2b;
  --el-table-row-hover-bg-color: rgba(143, 241, 199, 0.1);
  --el-table-text-color: rgba(255, 246, 234, 0.84);
  --el-table-header-text-color: #fff6ea;
  --el-table-border-color: rgba(255, 255, 255, 0.1);
  --el-fill-color-lighter: rgba(143, 241, 199, 0.1);
  background: transparent;
}

.profile-table:deep(.el-table__inner-wrapper::before) {
  background-color: rgba(255, 255, 255, 0.1);
}

.profile-table:deep(.el-table-fixed-column--right) {
  background: #0a1927;
}

.profile-table:deep(.el-table__body tr:hover > .el-table-fixed-column--right) {
  background: #102b34;
}

.profile-table:deep(.el-table__header-wrapper),
.profile-table:deep(.el-table__body-wrapper) {
  max-width: 100%;
}

.review-metric-grid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.review-list {
  display: grid;
  gap: 14px;
  min-height: 180px;
}

.review-card {
  padding: 16px;
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 20px;
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.09), rgba(255, 255, 255, 0.035)),
    rgba(5, 13, 23, 0.66);
}

.review-card.WARNING {
  border-color: rgba(255, 208, 138, 0.25);
  background:
    linear-gradient(135deg, rgba(255, 208, 138, 0.1), rgba(255, 255, 255, 0.035)),
    rgba(5, 13, 23, 0.66);
}

.review-card.DANGER {
  border-color: rgba(255, 105, 105, 0.28);
  background:
    linear-gradient(135deg, rgba(240, 68, 97, 0.12), rgba(255, 255, 255, 0.035)),
    rgba(5, 13, 23, 0.66);
}

.review-card-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.review-card-head span,
.review-proof-grid em {
  display: block;
  color: rgba(143, 241, 199, 0.82);
  font-size: 12px;
  font-style: normal;
}

.review-card-head h3 {
  margin: 6px 0;
  color: #fff;
  font-size: 22px;
}

.review-card-head p,
.review-content,
.review-decision p {
  margin: 0;
  color: rgba(255, 246, 234, 0.64);
  line-height: 1.7;
}

.review-content {
  margin-top: 12px;
}

.review-proof-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.review-proof-grid span,
.review-decision {
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.055);
}

.review-proof-grid b {
  display: block;
  margin-top: 7px;
  color: #fff;
  font-weight: 800;
  word-break: break-word;
}

.review-decision {
  margin-top: 12px;
}

.review-decision strong {
  display: block;
  margin-bottom: 7px;
  color: #ffd08a;
}

.review-decision p {
  word-break: break-all;
}

.review-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.profile-table strong,
.profile-table small {
  display: block;
}

.published-task-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 10px;
  min-width: 210px;
}

.published-task-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.profile-table small {
  margin-top: 6px;
  color: rgba(255, 246, 234, 0.56);
}

.table-receipt-btn,
.table-delete-btn {
  height: 32px;
  padding: 0 12px;
  margin-right: 8px;
  border-radius: 999px;
  font-weight: 800;
  cursor: pointer;
}

.table-receipt-btn {
  border: 1px solid rgba(143, 241, 199, 0.48);
  color: #062018;
  background: #8ff1c7;
  box-shadow: 0 8px 18px rgba(143, 241, 199, 0.16);
}

.table-receipt-btn.secondary {
  color: #fff6ea;
  border-color: rgba(255, 246, 234, 0.18);
  background: rgba(255, 255, 255, 0.08);
}

.table-delete-btn {
  border: 1px solid #f04438;
  color: #ffffff;
  background: #e5483f;
  box-shadow: 0 8px 18px rgba(229, 72, 63, 0.18);
}

.table-delete-btn:hover:not(:disabled) {
  background: #c92f28;
}

.table-delete-btn:disabled {
  color: #9f3a34;
  border-color: #ffc9c2;
  background: #fff1ef;
  box-shadow: none;
  opacity: 1;
  cursor: not-allowed;
}

.receipt-panel {
  color: #f6fbff;
}

.receipt-dialog :deep(.el-dialog) {
  border-radius: 24px;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 0%, rgba(143, 241, 199, 0.16), transparent 28%),
    linear-gradient(135deg, #06111e, #081827);
}

.receipt-dialog :deep(.el-dialog__header) {
  display: none;
}

.receipt-dialog :deep(.el-dialog__body) {
  padding: 24px;
}

.receipt-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.receipt-action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.receipt-head h2 {
  margin: 0 0 8px;
  font-size: 28px;
}

.receipt-head span,
.receipt-section p,
.receipt-meta span {
  color: rgba(246, 251, 255, 0.66);
}

.receipt-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.receipt-grid article,
.receipt-section {
  padding: 14px;
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.06);
}

.receipt-grid span {
  display: block;
  color: rgba(143, 241, 199, 0.82);
  font-size: 12px;
}

.receipt-grid strong {
  display: block;
  margin-top: 8px;
  font-size: 22px;
}

.receipt-section {
  margin-top: 12px;
}

.receipt-section strong {
  display: block;
  margin-bottom: 8px;
}

.receipt-section p {
  margin: 0;
  line-height: 1.7;
}

.receipt-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.receipt-meta span {
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
}

.review-receipt-section {
  border-color: rgba(255, 208, 138, 0.22);
  background: rgba(255, 208, 138, 0.07);
}

.receipt-digest {
  word-break: break-all;
  font-family: ui-monospace, monospace;
}

.order-log-dialog :deep(.el-dialog) {
  border-radius: 24px;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 0%, rgba(143, 241, 199, 0.16), transparent 28%),
    linear-gradient(135deg, #06111e, #081827);
}

.order-log-dialog :deep(.el-dialog__header) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}

.order-log-dialog :deep(.el-dialog__title),
.order-log-dialog :deep(.el-dialog__close) {
  color: #f6fbff;
}

.order-log-panel {
  color: #f6fbff;
}

.order-log-panel header {
  margin-bottom: 16px;
}

.order-log-panel h2 {
  margin: 0 0 8px;
  font-size: 28px;
}

.order-log-panel header span {
  color: rgba(246, 251, 255, 0.62);
}

.order-log-list {
  display: grid;
  gap: 10px;
  max-height: 540px;
  overflow: auto;
}

.order-log-list article {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  gap: 10px;
  padding: 13px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.055);
}

.order-log-list article > i {
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  background: #8ff1c7;
}

.order-log-list article.WARNING > i {
  background: #ffd08a;
}

.order-log-list article.DANGER > i {
  background: #ff6b6b;
}

.order-log-list span,
.order-log-list em {
  display: block;
  color: rgba(246, 251, 255, 0.52);
  font-size: 12px;
  font-style: normal;
}

.order-log-list strong {
  display: block;
  margin-top: 5px;
  color: #fff;
}

.order-log-list p {
  margin: 7px 0 0;
  color: rgba(246, 251, 255, 0.66);
  line-height: 1.65;
}

.order-log-list em {
  margin-top: 7px;
  word-break: break-all;
  font-family: ui-monospace, monospace;
}

.wallet-grid {
  display: grid;
  gap: 12px;
}

.chain-freeze-panel {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.35fr);
  gap: 16px;
  align-items: stretch;
  padding: 18px;
  margin-bottom: 16px;
  border: 1px solid rgba(143, 241, 199, 0.16);
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.1), rgba(255, 208, 138, 0.08)),
    rgba(5, 13, 23, 0.7);
}

.chain-freeze-panel h3 {
  margin: 0 0 10px;
  font-size: 26px;
}

.chain-freeze-panel span {
  color: rgba(255, 246, 234, 0.64);
  line-height: 1.7;
}

.freeze-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.freeze-metrics article {
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.07);
}

.freeze-metrics small,
.freeze-metrics em {
  display: block;
  color: rgba(255, 246, 234, 0.58);
  font-style: normal;
}

.freeze-metrics strong {
  display: block;
  margin: 8px 0;
  color: #fff;
  font-size: 24px;
}

.wallet-card {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border-radius: 20px;
  border: 1px solid rgba(143, 241, 199, 0.16);
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.1), rgba(255, 208, 138, 0.07)),
    rgba(5, 13, 23, 0.72);
}

.wallet-card img,
.wallet-avatar {
  width: 76px;
  height: 76px;
  border-radius: 18px;
  object-fit: cover;
}

.wallet-avatar {
  display: grid;
  place-items: center;
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
  font-weight: 900;
}

.wallet-card span,
.wallet-card em {
  color: rgba(143, 241, 199, 0.86);
  font-style: normal;
  font-size: 12px;
}

.wallet-card h3 {
  margin: 6px 0;
}

.wallet-card p {
  margin: 0;
  color: rgba(255, 246, 234, 0.6);
}

.wallet-archive-link {
  min-height: 30px;
  margin-top: 10px;
  padding: 0 10px;
  border: 1px solid rgba(143, 241, 199, 0.3);
  border-radius: 8px;
  color: #a4f6d1;
  background: rgba(143, 241, 199, 0.08);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.wallet-archive-link:hover {
  border-color: rgba(143, 241, 199, 0.62);
  background: rgba(143, 241, 199, 0.16);
}

.wallet-card strong {
  color: #fff;
  font-size: 24px;
}

.profile-form {
  max-width: 720px;
}

.profile-form :deep(.el-form-item__label) {
  color: rgba(255, 246, 234, 0.78);
}

.profile-form :deep(.el-input__inner) {
  color: #fff6ea;
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(5, 13, 23, 0.72);
}

.form-grid {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 14px;
}

@media (max-width: 980px) {
  .profile-layout {
    grid-template-columns: 1fr;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chain-freeze-panel,
  .freeze-metrics,
  .review-metric-grid,
  .review-proof-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .profile-page {
    padding: 18px;
  }

  .profile-header,
  .panel-head {
    flex-direction: column;
  }

  .metric-grid,
    .receipt-grid,
    .wallet-card,
    .chain-freeze-panel,
    .freeze-metrics,
    .review-metric-grid,
    .review-proof-grid,
    .form-grid {
    grid-template-columns: 1fr;
  }
}

@media print {
  body * {
    visibility: hidden;
  }

  .receipt-panel,
  .receipt-panel * {
    visibility: visible;
  }

  .receipt-panel {
    position: fixed;
    inset: 0;
    padding: 24px;
    color: #111827;
    background: #fff;
  }

  .receipt-action-row {
    display: none;
  }
}
</style>
